import { createFileRoute, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { ArrowLeft, ArrowRight, Check, MessageCircle, Nfc, QrCode } from "lucide-react";

import { Footer } from "@/components/site/Footer";
import { Header } from "@/components/site/Header";
import { ResponsiveImage } from "@/components/site/ResponsiveImage";
import { WhatsAppFloat } from "@/components/site/WhatsAppFloat";
import { track, trackOnce } from "@/lib/analytics";
import { buscarCaso, casosReales, type CasoReal } from "@/lib/casos";
import { WHATSAPP } from "@/lib/contacto";

const base = "https://pymevisible.cl/casos";

export const Route = createFileRoute("/casos_/$slug")({
  beforeLoad: ({ params }) => {
    if (!buscarCaso(params.slug)) throw notFound();
  },
  notFoundComponent: CasoNoEncontrado,
  component: CasoDetallePage,
  head: ({ params }) => {
    const caso = buscarCaso(params.slug);
    const canonical = `${base}/${params.slug}`;
    const title = caso
      ? `${caso.cliente}: stand NFC + QR | Pyme Visible`
      : "Caso no encontrado | Pyme Visible";
    const description = caso?.resumen ?? "Conoce trabajos reales realizados por Pyme Visible.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: canonical },
        ...(caso
          ? [
              { property: "og:image", content: `https://pymevisible.cl${caso.imagen}` },
              { name: "twitter:image", content: `https://pymevisible.cl${caso.imagen}` },
            ]
          : []),
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: canonical }],
      scripts: caso
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@graph": [
                  {
                    "@type": "WebPage",
                    "@id": `${canonical}#webpage`,
                    url: canonical,
                    name: title,
                    description,
                    primaryImageOfPage: {
                      "@type": "ImageObject",
                      url: `https://pymevisible.cl${caso.imagen}`,
                    },
                    isPartOf: { "@id": "https://pymevisible.cl/#website" },
                  },
                  {
                    "@type": "BreadcrumbList",
                    itemListElement: [
                      {
                        "@type": "ListItem",
                        position: 1,
                        name: "Inicio",
                        item: "https://pymevisible.cl/",
                      },
                      { "@type": "ListItem", position: 2, name: "Casos reales", item: base },
                      { "@type": "ListItem", position: 3, name: caso.cliente, item: canonical },
                    ],
                  },
                ],
              }),
            },
          ]
        : [],
    };
  },
});

function CasoDetallePage() {
  const { slug } = Route.useParams();
  const caso = buscarCaso(slug);
  if (!caso) return <CasoNoEncontrado />;
  return <CasoDetalle caso={caso} />;
}

function CasoDetalle({ caso }: { caso: CasoReal }) {
  useEffect(
    () => trackOnce(`case-study:${caso.slug}`, "case_study_view", { cliente: caso.cliente }),
    [caso],
  );
  const mensaje = `${WHATSAPP.split("?")[0]}?text=${encodeURIComponent(`Hola, vi el caso de ${caso.cliente} y quiero cotizar un producto NFC para mi negocio.`)}`;
  const relacionados = casosReales.filter((item) => item.slug !== caso.slug).slice(0, 3);
  return (
    <div className="pv-cases-page min-h-screen bg-canvas">
      <Header productLinksNewTab={false} />
      <main id="contenido">
        <article>
          <header className="pv-page-hero pv-case-detail-hero border-b border-ink/10">
            <div className="mx-auto grid max-w-[1320px] gap-8 px-5 py-8 sm:px-8 sm:py-16 lg:grid-cols-[.82fr_1.18fr] lg:items-center lg:px-10">
              <div>
                <nav
                  aria-label="Ruta de navegación"
                  className="pv-breadcrumb inline-flex max-w-full flex-wrap items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-slate sm:text-sm"
                >
                  <a href="/" className="hover:text-ink hover:underline">
                    Inicio
                  </a>
                  <span aria-hidden="true" className="text-primary/60">
                    /
                  </span>
                  <a href="/casos" className="hover:text-ink hover:underline">
                    Casos
                  </a>
                  <span aria-hidden="true" className="text-primary/60">
                    /
                  </span>
                  <span className="text-ink">{caso.cliente}</span>
                </nav>
                <p className="mt-7 font-mono text-xs uppercase tracking-[0.16em] text-primary">
                  Caso real · {caso.rubro}
                </p>
                <h1 className="mt-3 font-display text-5xl font-extrabold uppercase leading-[0.9] text-ink sm:text-7xl">
                  {caso.cliente}
                </h1>
                <p className="mt-5 text-lg font-semibold text-ink">{caso.producto}</p>
                <p className="mt-4 max-w-2xl leading-relaxed text-slate sm:text-lg">
                  {caso.resumen}
                </p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <a
                    href={`/productos/${caso.productoId}`}
                    onClick={() =>
                      track("case_product_click", {
                        cliente: caso.cliente,
                        producto: caso.producto,
                      })
                    }
                    className="pv-button pv-button--primary inline-flex min-h-12 items-center gap-2 px-6 py-3.5"
                  >
                    Ver producto <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </a>
                  <a
                    href={mensaje}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      track("whatsapp_click", { ubicacion: "caso_real", cliente: caso.cliente })
                    }
                    className="pv-button pv-button--secondary inline-flex min-h-12 items-center gap-2 px-6 py-3.5"
                  >
                    <MessageCircle className="h-5 w-5" aria-hidden="true" /> Cotizar
                  </a>
                </div>
              </div>
              <figure className="pv-card pv-case-hero-media overflow-hidden rounded-[2rem] bg-white p-3 sm:rounded-[3rem] sm:p-4">
                <ResponsiveImage
                  sizes="(max-width: 639px) 90vw, 560px"
                  fetchPriority="high"
                  src={caso.imagen}
                  alt={caso.alt}
                  width={1200}
                  height={1600}
                  className="max-h-[720px] w-full rounded-[1.5rem] object-cover sm:rounded-[2.5rem]"
                />
              </figure>
            </div>
          </header>

          <section className="mx-auto grid max-w-[1320px] gap-8 px-5 py-10 sm:px-8 sm:py-18 lg:grid-cols-[.9fr_1.1fr] lg:px-10">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
                La entrega
              </p>
              <h2 className="pv-section-title mt-3">Qué se preparó.</h2>
              <dl className="mt-7 grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
                {[
                  ["Negocio", caso.cliente],
                  ["Producto", caso.producto],
                  ["Destino", caso.destino],
                ].map(([etiqueta, valor]) => (
                  <div
                    key={etiqueta}
                    className="pv-case-fact rounded-2xl border border-ink/10 bg-white p-4"
                  >
                    <dt className="font-mono text-[0.65rem] uppercase tracking-[0.14em] text-primary">
                      {etiqueta}
                    </dt>
                    <dd className="mt-1 font-bold text-ink">{valor}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="pv-card pv-case-panel rounded-[2rem] bg-white p-6 sm:p-9">
              <h2 className="font-display text-3xl font-extrabold uppercase text-ink sm:text-4xl">
                Incluido en el trabajo
              </h2>
              <ul className="mt-6 grid gap-4">
                {caso.entrega.map((item) => (
                  <li key={item} className="flex gap-3 leading-relaxed text-slate">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
              <div className="mt-8 grid grid-cols-2 gap-px overflow-hidden rounded-2xl bg-ink/10">
                <div className="bg-canvas p-5">
                  <Nfc className="h-6 w-6 text-primary" aria-hidden="true" />
                  <p className="mt-3 font-bold text-ink">Acercar</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate">
                    El teléfono detecta el chip NFC.
                  </p>
                </div>
                <div className="bg-canvas p-5">
                  <QrCode className="h-6 w-6 text-primary" aria-hidden="true" />
                  <p className="mt-3 font-bold text-ink">Escanear</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate">
                    El QR funciona como alternativa.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {caso.secundaria && (
            <section className="mx-auto max-w-[1320px] px-5 pb-10 sm:px-8 sm:pb-18 lg:px-10">
              <ResponsiveImage
                sizes="(max-width: 639px) 90vw, 640px"
                src={caso.secundaria.imagen}
                alt={caso.secundaria.alt}
                width={1200}
                height={1600}
                loading="lazy"
                className="pv-case-secondary mx-auto max-h-[760px] w-full rounded-[2rem] object-cover sm:rounded-[3rem]"
              />
            </section>
          )}

          {caso.testimonio && (
            <section className="pv-dark-section text-white">
              <div className="mx-auto max-w-[1000px] px-5 py-12 text-center sm:px-8 sm:py-20">
                <p className="font-mono text-xs uppercase tracking-[0.16em] text-signal">
                  Testimonio del cliente
                </p>
                <blockquote className="mt-5 font-display text-3xl font-bold leading-tight sm:text-5xl">
                  “{caso.testimonio.texto}”
                </blockquote>
                <p className="mt-5 text-sm text-white/70">
                  {caso.testimonio.autor} · {caso.cliente}
                </p>
                {caso.testimonio.url && (
                  <a
                    href={caso.testimonio.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-block text-sm font-semibold text-signal underline underline-offset-4"
                  >
                    Ver opinión pública en Google
                  </a>
                )}
              </div>
            </section>
          )}

          <section className="mx-auto max-w-[1320px] px-5 py-10 sm:px-8 sm:py-18 lg:px-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.16em] text-primary">
                  Más trabajos
                </p>
                <h2 className="pv-section-title mt-2">Otros casos reales.</h2>
              </div>
              <a href="/casos" className="font-bold text-ink underline underline-offset-4">
                Ver todos
              </a>
            </div>
            <div className="mt-7 grid gap-4 sm:grid-cols-3">
              {relacionados.map((item) => (
                <a
                  key={item.slug}
                  href={`/casos/${item.slug}`}
                  className="pv-card pv-case-related-card group overflow-hidden rounded-2xl bg-white p-3"
                >
                  <ResponsiveImage
                    sizes="(max-width: 639px) 90vw, 390px"
                    src={item.imagen}
                    alt=""
                    width={600}
                    height={700}
                    loading="lazy"
                    className="aspect-[4/3] w-full rounded-xl object-cover"
                  />
                  <div className="p-3">
                    <p className="font-bold text-ink">{item.cliente}</p>
                    <span className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                      Ver caso{" "}
                      <ArrowRight
                        className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </section>
        </article>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

function CasoNoEncontrado() {
  return (
    <div className="min-h-screen bg-canvas">
      <Header productLinksNewTab={false} />
      <main
        id="contenido"
        className="mx-auto grid min-h-[65vh] max-w-[1320px] place-items-center px-5 text-center"
      >
        <div>
          <h1 className="font-display text-5xl font-extrabold uppercase text-ink">
            Caso no encontrado
          </h1>
          <a
            href="/casos"
            className="pv-button pv-button--primary mt-7 inline-flex items-center gap-2 px-6 py-3"
          >
            <ArrowLeft className="h-4 w-4" /> Ver casos reales
          </a>
        </div>
      </main>
      <Footer />
    </div>
  );
}
